import { useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, KeyRound, Copy, Check, Download, PlayCircle, FileText } from "lucide-react";

const PRESETS = [
  { v: "1month", label: "1 Month" },
  { v: "3months", label: "3 Months" },
  { v: "1year", label: "1 Year" },
  { v: "5years", label: "5 Years" },
  { v: "custom", label: "Custom Date" },
];

export default function GenerateTokenModal({ open, onClose, onCreated }) {
  const { user, refresh } = useAuth();
  const exhausted = user?.role === "client" && user?.token_quota != null && (user?.tokens_remaining ?? 0) <= 0;
  const [form, setForm] = useState({
    candidate_name: "",
    candidate_email: "",
    interview_id: "",
    validity_preset: "1month",
    custom_expires_at: "",
    self_destruct: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState("");

  if (!open) return null;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.candidate_name.trim()) {
      setError("Candidate name is required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/tokens", form);
      setCreated(data);
      onCreated?.();
      refresh?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setCreated(null);
    setForm({
      candidate_name: "",
      candidate_email: "",
      interview_id: "",
      validity_preset: "1month",
      custom_expires_at: "",
      self_destruct: true,
    });
    onClose();
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const downloadUrl = created ? `${API}/agent/download?token=${created.token}` : "";
  const simUrl = created ? `${window.location.origin}/scan/${created.token}` : "";
  const winCmd = created ? `irm "${API}/agent/install.ps1?token=${created.token}" | iex` : "";
  const macCmd = created ? `curl -fsSL "${API}/agent/install.sh?token=${created.token}" | bash` : "";

  const field =
    "w-full bg-[#0B1220] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-colors";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={reset} />
      <div
        data-testid="generate-token-modal"
        className="relative card-soc w-full max-w-lg glow-blue fade-up max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
          <div className="flex items-center gap-2.5">
            <KeyRound className="w-5 h-5 text-cyan-400" />
            <h3 className="font-head font-bold text-lg">
              {created ? "Scan Token Ready" : "Generate One-Time Scan Token"}
            </h3>
          </div>
          <button onClick={reset} data-testid="close-modal-button" className="text-slate-500 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!created ? (
          <div className="p-6 space-y-4">
            <div>
              <label className="eyebrow block mb-1.5">Candidate Name *</label>
              <input
                data-testid="candidate-name-input"
                className={field}
                placeholder="e.g. Jordan Rivera"
                value={form.candidate_name}
                onChange={(e) => upd("candidate_name", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="eyebrow block mb-1.5">Candidate Email</label>
                <input
                  data-testid="candidate-email-input"
                  className={field}
                  placeholder="candidate@mail.com"
                  value={form.candidate_email}
                  onChange={(e) => upd("candidate_email", e.target.value)}
                />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Interview ID</label>
                <input
                  data-testid="interview-id-input"
                  className={field}
                  placeholder="INT-2026-014"
                  value={form.interview_id}
                  onChange={(e) => upd("interview_id", e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Validity Window</label>
              <div className="flex flex-wrap gap-2" data-testid="validity-window-select">
                {PRESETS.map((p) => (
                  <button
                    key={p.v}
                    data-testid={`validity-${p.v}`}
                    onClick={() => upd("validity_preset", p.v)}
                    className={`px-3 py-2 rounded-lg text-xs font-mono border transition-colors ${
                      form.validity_preset === p.v
                        ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300"
                        : "border-white/10 text-slate-400 hover:border-white/25"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {form.validity_preset === "custom" && (
                <input
                  type="date"
                  data-testid="custom-date-input"
                  className={`${field} mt-3`}
                  value={form.custom_expires_at}
                  onChange={(e) => upd("custom_expires_at", e.target.value)}
                />
              )}
            </div>
            <label className="flex items-center justify-between bg-[#0B1220] border border-white/10 rounded-lg px-3.5 py-3 cursor-pointer">
              <div>
                <div className="text-sm text-slate-200 font-medium">Self-destruct executable</div>
                <div className="text-xs text-slate-500">Delete the scanner after a successful scan</div>
              </div>
              <input
                type="checkbox"
                data-testid="self-destruct-toggle"
                checked={form.self_destruct}
                onChange={(e) => upd("self_destruct", e.target.checked)}
                className="w-5 h-5 accent-cyan-500"
              />
            </label>

            {error && <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2" data-testid="token-error">{error}</div>}

            {user?.role === "client" && user?.token_quota != null && (
              <div className={`text-xs font-mono px-3 py-2 rounded-lg ${exhausted ? "text-red-400 bg-red-500/10" : "text-slate-400 bg-white/5"}`} data-testid="quota-remaining">
                {exhausted
                  ? `Quota exhausted (${user.tokens_generated}/${user.token_quota}). Ask your administrator to increase it.`
                  : `${user.tokens_remaining} of ${user.token_quota} tokens remaining`}
              </div>
            )}

            <button
              onClick={submit}
              disabled={loading || exhausted}
              data-testid="submit-generate-token-button"
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#04121a] font-semibold rounded-lg py-3 transition-colors disabled:opacity-50"
            >
              {loading ? "Generating…" : "Generate Token"}
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div>
              <label className="eyebrow block mb-1.5">One-Time Token</label>
              <div className="flex items-center gap-2">
                <code className="term flex-1 px-3 py-2.5 text-cyan-300 truncate" data-testid="created-token-value">
                  {created.token}
                </code>
                <button
                  onClick={() => copy(created.token, "tok")}
                  className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"
                >
                  {copied === "tok" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="eyebrow block mb-1.5">One-Click Install · Windows PowerShell (no Python needed)</label>
              <div className="flex items-start gap-2">
                <code className="term flex-1 px-3 py-2.5 text-slate-300 break-all text-xs" data-testid="win-one-click-command">{winCmd}</code>
                <button
                  onClick={() => copy(winCmd, "win")}
                  data-testid="copy-win-command"
                  className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 shrink-0"
                >
                  {copied === "win" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">One-Click Install · macOS Terminal</label>
              <div className="flex items-start gap-2">
                <code className="term flex-1 px-3 py-2.5 text-slate-300 break-all text-xs" data-testid="mac-one-click-command">{macCmd}</code>
                <button
                  onClick={() => copy(macCmd, "mac")}
                  data-testid="copy-mac-command"
                  className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 shrink-0"
                >
                  {copied === "mac" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="eyebrow block mb-1.5">Candidate Invite Link</label>
              <div className="flex items-center gap-2">
                <code className="term flex-1 px-3 py-2.5 text-emerald-300 truncate text-xs" data-testid="invite-link-value">
                  {simUrl}
                </code>
                <button
                  onClick={() => copy(simUrl, "invite")}
                  data-testid="copy-invite-link-button"
                  className="p-2.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 shrink-0"
                >
                  {copied === "invite" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                Send this link to the candidate — it opens the one-time scanner on their machine.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {/* <a
                href={downloadUrl}
                data-testid="download-scanner-link"
                className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-2.5 text-sm text-slate-200"
              >
                <Download className="w-4 h-4" /> Scanner .py
              </a> */}
              <a
                href={`${API}/tokens/${created.id}/instructions.pdf`}
                data-testid="download-instructions-link"
                className="flex items-center justify-center gap-2 bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 rounded-lg py-2.5 text-sm text-cyan-300"
              >
                <FileText className="w-4 h-4" /> Instructions PDF
              </a>
              <a
                href={simUrl}
                data-testid="run-simulator-link"
                className="flex items-center justify-center gap-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 rounded-lg py-2.5 text-sm text-emerald-300"
              >
                <PlayCircle className="w-4 h-4" /> Candidate Page
              </a>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed">
              This token is <span className="text-slate-300">one-time use</span> and expires per the selected validity
              window. The candidate page runs a real browser-level device check and waits for the real deep-scan result
              from the scanner (<span className="font-mono">.py</span>, or package it into a Windows{" "}
              <span className="font-mono">.exe</span> / macOS <span className="font-mono">.app</span> — see Scanner Agent page).
            </p>

            <button
              onClick={reset}
              className="w-full border border-white/10 hover:bg-white/5 text-slate-300 rounded-lg py-2.5 text-sm"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
